import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import type { PersistConfig } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import appSlice from 'common/store/appSlice';
import authSlice from 'features/login/authSlice';
import revenueCatReducer from 'features/login/revenueCatSlice';
import onboardingReducer from 'features/Onboarding/onboardingSlice';
import lockReducer from 'common/store/lockSlice';
import spotlightReducer from 'common/store/spotlightSlice';
import { rtkQueryErrorHandler } from 'common/utils/rtkErrorHandler';
import { baseApi } from 'common/store/baseApi';
import guestSessionReducer from 'features/GuestSession/guestSessionSlice';
import shepherdReducer from 'features/Shepherd/shepherdSlice';
import { bindStore } from 'common/utils/mixpanel/eventTriggers';

// Strip password from the persisted snapshot of the app slice.
// Live in-memory state is unaffected (outbound pass-through).
const stripPasswordTransform = createTransform(
  (inboundState: any, key) => {
    if (key === 'app' && inboundState?.user?.password) {
      return { ...inboundState, user: { ...inboundState.user, password: '' } };
    }
    return inboundState;
  },
  (outboundState: any) => outboundState,
  { whitelist: ['app'] },
);

// Never persist auth.lastValidatedAt. It exists only to dedupe the boot
// double-validate within a live session; persisting it would let a cold boot
// that happened <60s after the last validate skip its one-and-only validate,
// so we strip it on write. Rehydrate then keeps initialState's null and the
// first boot always validates exactly once.
const stripLastValidatedAtTransform = createTransform(
  (inboundState: any, key) => {
    if (key === 'auth' && inboundState) {
      return { ...inboundState, lastValidatedAt: null };
    }
    return inboundState;
  },
  (outboundState: any) => outboundState,
  { whitelist: ['auth'] },
);

export const rootReducer = combineReducers({
  app: appSlice,
  auth: authSlice,
  revenueCat: revenueCatReducer,
  onboarding: onboardingReducer,
  lock: lockReducer,
  spotlight: spotlightReducer,
  guestSession: guestSessionReducer,
  shepherd: shepherdReducer,
  [baseApi.reducerPath]: baseApi.reducer,
});

const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
  key: 'root',
  version: 2,
  storage,
  whitelist: ['app', 'auth', 'revenueCat', 'onboarding', 'lock', 'spotlight', 'guestSession', 'shepherd'],
  blacklist: [baseApi.reducerPath],
  transforms: [stripPasswordTransform, stripLastValidatedAtTransform],
  migrate: (state: any) => {
    // v1 → v2: clear any password that was previously persisted to localStorage
    if (state?.app?.user?.password) {
      // eslint-disable-next-line no-param-reassign
      state.app.user.password = '';
    }
    return Promise.resolve(state);
  },
};

export const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    },
  }).concat(baseApi.middleware, rtkQueryErrorHandler),
});

// Hand trackEvent its store reference (see eventTriggers.ts - importing the
// store from there would recreate the import cycle the linter flags).
bindStore(store);

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
